import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { 
  getAllFloorPlansFromSupabase, 
  getAllInventoriesFromSupabase,
  deleteFloorPlanFromSupabase,
  deleteInventoryFromSupabase,
  testSupabaseConnection,
  loadFloorPlanFromSupabase,
  loadInventoryFromSupabase
} from './supabaseStorage';
import OfflineStorageService from '../services/offlineStorage';

export interface FileCleanupReport {
  duplicateFiles: DuplicateFileGroup[];
  orphanedFiles: OrphanedFile[];
  inconsistentFiles: InconsistentFile[];
  cleanupActions: CleanupAction[];
  summary: {
    totalIssues: number;
    safeDeletions: number;
    requiresManualReview: number;
    storageFreed: number; // in bytes
  };
}

export interface DuplicateFileGroup {
  name: string;
  type: 'floorplan' | 'inventory';
  files: FileEntry[];
  recommendedAction: 'keep-newest' | 'keep-cloud' | 'manual-review';
  reason: string;
}

export interface OrphanedFile {
  id: string;
  name: string;
  type: 'floorplan' | 'inventory';
  location: 'supabase' | 'local' | 'offline';
  issue: string;
  canAutoDelete: boolean;
}

export interface InconsistentFile {
  id: string;
  name: string;
  type: 'floorplan' | 'inventory';
  issues: string[];
  suggestedFix: string;
}

export interface FileEntry {
  id: string;
  name: string;
  source: 'supabase' | 'local' | 'offline';
  isLocal: boolean;
  hasValidData: boolean;
  size?: number;
  createdAt?: string;
  updatedAt?: string;
  cloudId?: string;
}

export interface CleanupAction {
  type: 'delete' | 'merge' | 'repair';
  fileId: string;
  fileName: string;
  source: 'supabase' | 'local' | 'offline';
  safe: boolean;
  description: string;
}

/**
 * Main function to analyze and generate a cleanup report for duplicate/orphaned files
 */
export const analyzeFileSystem = async (): Promise<FileCleanupReport> => {
  console.log('[FileCleanup] Starting comprehensive file system analysis...');
  
  const report: FileCleanupReport = {
    duplicateFiles: [],
    orphanedFiles: [],
    inconsistentFiles: [],
    cleanupActions: [],
    summary: {
      totalIssues: 0,
      safeDeletions: 0,
      requiresManualReview: 0,
      storageFreed: 0
    }
  };

  try {
    // Step 1: Collect all files from all sources
    const allFiles = await collectAllFiles();
    console.log(`[FileCleanup] Collected ${allFiles.length} files from all sources`);

    // Step 2: Identify duplicates by name and type
    const duplicateGroups = identifyDuplicates(allFiles);
    report.duplicateFiles = duplicateGroups;

    // Step 3: Identify orphaned files (files that exist in DB but have no data)
    const orphanedFiles = await identifyOrphanedFiles(allFiles);
    report.orphanedFiles = orphanedFiles;

    // Step 4: Identify inconsistent files (files with mismatched metadata)
    const inconsistentFiles = identifyInconsistentFiles(allFiles);
    report.inconsistentFiles = inconsistentFiles;

    // Step 5: Generate cleanup actions
    const cleanupActions = generateCleanupActions(duplicateGroups, orphanedFiles, inconsistentFiles);
    report.cleanupActions = cleanupActions;

    // Step 6: Calculate summary
    report.summary = calculateSummary(duplicateGroups, orphanedFiles, inconsistentFiles, cleanupActions);

    console.log('[FileCleanup] Analysis complete:', report.summary);
    return report;

  } catch (error) {
    console.error('[FileCleanup] Error during analysis:', error);
    throw error;
  }
};

/**
 * Collect files from all storage sources
 */
const collectAllFiles = async (): Promise<FileEntry[]> => {
  const allFiles: FileEntry[] = [];
  
  // Get files from Supabase
  const connectionTest = await testSupabaseConnection();
  if (connectionTest.connected) {
    try {
      const [supabaseFloorPlans, supabaseInventories] = await Promise.all([
        getAllFloorPlansFromSupabase(),
        getAllInventoriesFromSupabase()
      ]);

      // Add Supabase floor plans
      for (const fp of supabaseFloorPlans) {
        const hasValidData = await validateSupabaseFile(fp.id, 'floorplan');
        allFiles.push({
          id: fp.id,
          name: fp.name,
          source: 'supabase',
          isLocal: false,
          hasValidData,
          createdAt: fp.created_at,
          updatedAt: fp.updated_at,
          cloudId: fp.id
        });
      }

      // Add Supabase inventories  
      for (const inv of supabaseInventories) {
        const hasValidData = await validateSupabaseFile(inv.id, 'inventory');
        allFiles.push({
          id: inv.id,
          name: inv.name,
          source: 'supabase',
          isLocal: false,
          hasValidData,
          createdAt: inv.created_at,
          updatedAt: inv.updated_at,
          cloudId: inv.id
        });
      }
    } catch (error) {
      console.warn('[FileCleanup] Error loading Supabase files:', error);
    }
  }

  // Get files from offline storage
  const offlineService = OfflineStorageService.getInstance();
  const offlineFiles = offlineService.getOfflineFiles();
  
  for (const offlineFile of offlineFiles) {
    allFiles.push({
      id: offlineFile.id,
      name: offlineFile.name,
      source: 'offline',
      isLocal: true,
      hasValidData: true, // Assume offline files are valid
      createdAt: offlineFile.lastModifiedAt,
      updatedAt: offlineFile.lastModifiedAt,
      cloudId: offlineFile.cloudId
    });
  }

  // Get legacy local files
  try {
    const directories = ['floorplans', 'inventories'];
    
    for (const dir of directories) {
      try {
        const result = await Filesystem.readdir({
          path: dir,
          directory: Directory.Documents
        });

        for (const file of result.files) {
          if (file.name.endsWith('.json')) {
            const nameWithoutExtension = file.name.replace('.json', '');
            const hasValidData = await validateLocalFile(dir, file.name);
            
            allFiles.push({
              id: file.name,
              name: nameWithoutExtension,
              source: 'local',
              isLocal: true,
              hasValidData,
              size: file.size,
              createdAt: file.ctime ? new Date(file.ctime).toISOString() : undefined,
              updatedAt: file.mtime ? new Date(file.mtime).toISOString() : undefined
            });
          }
        }
      } catch (dirError) {
        console.warn(`[FileCleanup] Could not read ${dir} directory:`, dirError);
      }
    }
  } catch (error) {
    console.warn('[FileCleanup] Error loading local files:', error);
  }

  return allFiles;
};

/**
 * Validate if a Supabase file has valid data
 */
const validateSupabaseFile = async (fileId: string, type: 'floorplan' | 'inventory'): Promise<boolean> => {
  try {
    if (type === 'floorplan') {
      const data = await loadFloorPlanFromSupabase(fileId);
      return data != null && typeof data === 'object';
    } else {
      const data = await loadInventoryFromSupabase(fileId);
      return data != null && typeof data === 'object';
    }
  } catch (error) {
    console.warn(`[FileCleanup] Invalid Supabase file ${fileId}:`, error);
    return false;
  }
};

/**
 * Validate if a local file has valid data
 */
const validateLocalFile = async (directory: string, fileName: string): Promise<boolean> => {
  try {
    const result = await Filesystem.readFile({
      path: `${directory}/${fileName}`,
      directory: Directory.Documents,
      encoding: Encoding.UTF8
    });
    
    const data = JSON.parse(result.data as string);
    return data != null && typeof data === 'object';
  } catch (error) {
    console.warn(`[FileCleanup] Invalid local file ${fileName}:`, error);
    return false;
  }
};

/**
 * Identify duplicate files by name and type
 */
const identifyDuplicates = (files: FileEntry[]): DuplicateFileGroup[] => {
  const duplicateGroups: DuplicateFileGroup[] = [];
  const filesByNameAndType = new Map<string, FileEntry[]>();

  // Group files by name and type
  files.forEach(file => {
    const key = `${file.name}_${getFileType(file)}`;
    if (!filesByNameAndType.has(key)) {
      filesByNameAndType.set(key, []);
    }
    filesByNameAndType.get(key)!.push(file);
  });

  // Find groups with duplicates
  filesByNameAndType.forEach((groupFiles, key) => {
    if (groupFiles.length > 1) {
      const [name, type] = key.split('_');
      const recommendedAction = determineRecommendedAction(groupFiles);
      
      duplicateGroups.push({
        name,
        type: type as 'floorplan' | 'inventory',
        files: groupFiles,
        recommendedAction,
        reason: getRecommendationReason(groupFiles, recommendedAction)
      });
    }
  });

  return duplicateGroups;
};

/**
 * Identify orphaned files that exist in database but have no valid data
 */
const identifyOrphanedFiles = async (files: FileEntry[]): Promise<OrphanedFile[]> => {
  const orphanedFiles: OrphanedFile[] = [];

  for (const file of files) {
    if (!file.hasValidData) {
      orphanedFiles.push({
        id: file.id,
        name: file.name,
        type: getFileType(file),
        location: file.source,
        issue: 'File exists in database but has no valid data',
        canAutoDelete: true
      });
    }

    // Check for numeric-only IDs (likely database IDs without proper names)
    if (/^\d+$/.test(file.id) && file.name === file.id) {
      orphanedFiles.push({
        id: file.id,
        name: file.name,
        type: getFileType(file),
        location: file.source,
        issue: 'File has numeric-only ID without proper name',
        canAutoDelete: file.source !== 'local' // Don't auto-delete local files
      });
    }
  }

  return orphanedFiles;
};

/**
 * Identify files with inconsistent metadata
 */
const identifyInconsistentFiles = (files: FileEntry[]): InconsistentFile[] => {
  const inconsistentFiles: InconsistentFile[] = [];

  files.forEach(file => {
    const issues: string[] = [];

    // Check for missing or inconsistent names
    if (!file.name || file.name.trim() === '') {
      issues.push('Missing or empty name');
    }

    // Check for ID/name mismatch
    if (file.id !== file.name && !file.name.includes(file.id) && !file.id.includes(file.name)) {
      issues.push('ID and name mismatch');
    }

    // Check for inconsistent dates
    if (file.createdAt && file.updatedAt && new Date(file.createdAt) > new Date(file.updatedAt)) {
      issues.push('Created date is after updated date');
    }

    if (issues.length > 0) {
      inconsistentFiles.push({
        id: file.id,
        name: file.name,
        type: getFileType(file),
        issues,
        suggestedFix: generateSuggestedFix(file, issues)
      });
    }
  });

  return inconsistentFiles;
};

/**
 * Generate cleanup actions based on analysis
 */
const generateCleanupActions = (
  duplicateGroups: DuplicateFileGroup[],
  orphanedFiles: OrphanedFile[],
  inconsistentFiles: InconsistentFile[]
): CleanupAction[] => {
  const actions: CleanupAction[] = [];

  // Actions for duplicates
  duplicateGroups.forEach(group => {
    if (group.recommendedAction === 'keep-newest' || group.recommendedAction === 'keep-cloud') {
      const filesToDelete = group.files.slice(1); // Keep first, delete rest
      
      filesToDelete.forEach(file => {
        actions.push({
          type: 'delete',
          fileId: file.id,
          fileName: file.name,
          source: file.source,
          safe: true,
          description: `Delete duplicate of "${file.name}" from ${file.source}`
        });
      });
    }
  });

  // Actions for orphaned files
  orphanedFiles.forEach(orphan => {
    if (orphan.canAutoDelete) {
      actions.push({
        type: 'delete',
        fileId: orphan.id,
        fileName: orphan.name,
        source: orphan.location,
        safe: true,
        description: `Delete orphaned file: ${orphan.issue}`
      });
    }
  });

  return actions;
};

/**
 * Execute the cleanup actions
 */
export const executeCleanup = async (actions: CleanupAction[]): Promise<{
  successful: number;
  failed: number;
  errors: string[];
}> => {
  console.log(`[FileCleanup] Executing ${actions.length} cleanup actions...`);
  
  let successful = 0;
  let failed = 0;
  const errors: string[] = [];

  for (const action of actions) {
    try {
      if (action.type === 'delete') {
        await deleteFileBySource(action.fileId, action.source, getFileTypeFromId(action.fileId));
        console.log(`[FileCleanup] Successfully deleted ${action.fileName} from ${action.source}`);
        successful++;
      }
    } catch (error) {
      console.error(`[FileCleanup] Failed to execute action for ${action.fileName}:`, error);
      errors.push(`Failed to delete ${action.fileName}: ${(error as Error).message}`);
      failed++;
    }
  }

  console.log(`[FileCleanup] Cleanup complete. Successful: ${successful}, Failed: ${failed}`);
  return { successful, failed, errors };
};

/**
 * Delete a file from a specific source
 */
const deleteFileBySource = async (
  fileId: string, 
  source: 'supabase' | 'local' | 'offline',
  type: 'floorplan' | 'inventory'
): Promise<void> => {
  switch (source) {
    case 'supabase':
      if (type === 'floorplan') {
        await deleteFloorPlanFromSupabase(fileId);
      } else {
        await deleteInventoryFromSupabase(fileId);
      }
      break;
      
    case 'local':
      const directory = type === 'floorplan' ? 'floorplans' : 'inventories';
      await Filesystem.deleteFile({
        path: `${directory}/${fileId}`,
        directory: Directory.Documents
      });
      break;
      
    case 'offline':
      const offlineService = OfflineStorageService.getInstance();
      // Note: OfflineStorageService doesn't expose a direct delete method
      // This would need to be implemented or handled differently
      console.warn(`[FileCleanup] Offline file deletion not fully implemented for ${fileId}`);
      break;
  }
};

// Helper functions
const getFileType = (file: FileEntry): 'floorplan' | 'inventory' => {
  // Try to determine type from file properties or name
  if (file.name.toLowerCase().includes('inventory') || file.name.toLowerCase().includes('report')) {
    return 'inventory';
  }
  return 'floorplan';
};

const getFileTypeFromId = (fileId: string): 'floorplan' | 'inventory' => {
  if (fileId.toLowerCase().includes('inventory') || fileId.toLowerCase().includes('report')) {
    return 'inventory';
  }
  return 'floorplan';
};

const determineRecommendedAction = (files: FileEntry[]): 'keep-newest' | 'keep-cloud' | 'manual-review' => {
  const hasCloud = files.some(f => f.source === 'supabase');
  const hasLocal = files.some(f => f.source === 'local');
  
  if (hasCloud && hasLocal) {
    return 'keep-cloud';
  }
  
  if (files.length === 2) {
    return 'keep-newest';
  }
  
  return 'manual-review';
};

const getRecommendationReason = (files: FileEntry[], action: string): string => {
  switch (action) {
    case 'keep-cloud':
      return 'Cloud version is preferred over local copies';
    case 'keep-newest':
      return 'Keeping the most recently updated version';
    case 'manual-review':
      return 'Multiple files found - manual review recommended';
    default:
      return 'No specific reason';
  }
};

const generateSuggestedFix = (file: FileEntry, issues: string[]): string => {
  if (issues.includes('Missing or empty name')) {
    return 'Assign a proper name based on content or delete if invalid';
  }
  if (issues.includes('ID and name mismatch')) {
    return 'Verify file integrity and rename if necessary';
  }
  return 'Review file manually and fix inconsistencies';
};

const calculateSummary = (
  duplicateGroups: DuplicateFileGroup[],
  orphanedFiles: OrphanedFile[],
  inconsistentFiles: InconsistentFile[],
  cleanupActions: CleanupAction[]
) => {
  const totalIssues = duplicateGroups.length + orphanedFiles.length + inconsistentFiles.length;
  const safeDeletions = cleanupActions.filter(a => a.safe).length;
  const requiresManualReview = duplicateGroups.filter(g => g.recommendedAction === 'manual-review').length +
                               orphanedFiles.filter(o => !o.canAutoDelete).length;

  return {
    totalIssues,
    safeDeletions,
    requiresManualReview,
    storageFreed: 0 // Would need to calculate actual file sizes
  };
}; 